// Hex view for a single network flow. Opens when the user clicks the
// "bytes" link on a Connections or Flows row in the Network figure.
//
//   <FlowBytesDialog open jobID pcapSha dstIp dstPort? onClose />
//
// Downloads the job's pcap artifact once, caches the ArrayBuffer per
// job in a module-level map, extracts the matching flow with
// lib/pcap.ts, renders two classic hex panels (offset · 16 bytes ·
// ASCII) for the client→server and server→client streams. No TCP
// payload decoding yet — a byte is a byte; the user can still see HTTP
// headers / NanoCore handshake / TLS records inline.
//
// This is Tier 2 of the "ANY.RUN-like network inspector" plan: Tier 1
// (per-row Suricata badges) is already in NetTables. Tier 3 — matched-
// byte highlighting from Suricata's `payload-printable=yes` output —
// slots in on top of this component without changing its props.
import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { extractFlow, type FlowBytes } from "@/lib/pcap";

const PCAP_CACHE = new Map<string, Promise<ArrayBuffer>>();

function fetchPcap(jobID: string, sha: string): Promise<ArrayBuffer> {
  const key = `${jobID}|${sha}`;
  let p = PCAP_CACHE.get(key);
  if (!p) {
    p = fetch(api.artifactURL(jobID, sha), { credentials: "include" }).then(async (r) => {
      if (!r.ok) throw new Error(`pcap fetch: HTTP ${r.status}`);
      return r.arrayBuffer();
    });
    PCAP_CACHE.set(key, p);
    // Evict a failed fetch so the next open can retry.
    p.catch(() => PCAP_CACHE.delete(key));
  }
  return p;
}

/** 16 bytes per row, offset in the gutter, ASCII on the right. Printable
 *  ASCII (0x20-0x7e) is shown verbatim; everything else is a dot. */
function HexView({ bytes, label }: { bytes: Uint8Array; label: string }) {
  if (!bytes.length) {
    return (
      <div className="rounded border border-rule-strong bg-paper p-3 text-meta text-ink-mute">
        <span className="lens-eyebrow">{label}</span>
        <div className="mt-1">No bytes captured in this direction.</div>
      </div>
    );
  }
  // Cap rendered rows — a slow laptop choking on 2 MB of hex isn't the
  // UX we're going for. The user can request the full stream (TODO).
  const CAP = 4 * 1024;
  const shown = bytes.length > CAP ? bytes.subarray(0, CAP) : bytes;
  const rowCount = Math.ceil(shown.length / 16);
  const rows: { off: number; hex: string; ascii: string }[] = [];
  for (let i = 0; i < rowCount; i++) {
    const chunk = shown.subarray(i * 16, i * 16 + 16);
    const hex = Array.from(chunk, (b) => b.toString(16).padStart(2, "0")).join(" ");
    const ascii = Array.from(chunk, (b) => (b >= 0x20 && b <= 0x7e ? String.fromCharCode(b) : ".")).join("");
    rows.push({ off: i * 16, hex, ascii });
  }
  return (
    <div className="rounded border border-rule-strong bg-paper">
      <div className="flex items-center justify-between border-b border-rule-strong px-3 py-2">
        <span className="lens-eyebrow">{label}</span>
        <span className="text-meta text-ink-mute tabular-nums">
          {bytes.length.toLocaleString()} bytes
          {bytes.length > CAP && ` · showing first ${CAP.toLocaleString()}`}
        </span>
      </div>
      <div className="tscroll max-h-[52vh] overflow-auto">
        <pre className="m-0 whitespace-pre p-3 font-mono text-[11.5px] leading-[1.45] tabular-nums text-ink">
          {rows
            .map((r) => `${r.off.toString(16).padStart(8, "0")}  ${r.hex.padEnd(48, " ")}  ${r.ascii}`)
            .join("\n")}
        </pre>
      </div>
    </div>
  );
}

export function FlowBytesDialog({
  open,
  jobID,
  pcapSha,
  dstIp,
  dstPort,
  onClose,
}: {
  open: boolean;
  jobID: string;
  pcapSha: string | undefined;
  dstIp: string;
  dstPort?: number;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const [state, setState] = useState<
    | { kind: "idle" }
    | { kind: "loading" }
    | { kind: "error"; message: string }
    | { kind: "ready"; flow: FlowBytes }
  >({ kind: "idle" });

  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    else if (!open && d.open) d.close();
  }, [open]);

  useEffect(() => {
    if (!open) {
      setState({ kind: "idle" });
      return;
    }
    if (!pcapSha) {
      setState({ kind: "error", message: "This run has no pcap artifact yet — Suricata runs after collection, so the capture may still be uploading." });
      return;
    }
    let cancelled = false;
    setState({ kind: "loading" });
    fetchPcap(jobID, pcapSha)
      .then((buf) => {
        if (cancelled) return;
        try {
          const flow = extractFlow(buf, { dstIp, dstPort });
          setState({ kind: "ready", flow });
        } catch (e) {
          setState({ kind: "error", message: e instanceof Error ? e.message : String(e) });
        }
      })
      .catch((e) => {
        if (cancelled) return;
        setState({ kind: "error", message: e instanceof Error ? e.message : String(e) });
      });
    return () => {
      cancelled = true;
    };
  }, [open, jobID, pcapSha, dstIp, dstPort]);

  const title = useMemo(() => `${dstIp}${dstPort !== undefined ? `:${dstPort}` : ""}`, [dstIp, dstPort]);

  return (
    <dialog
      ref={ref}
      aria-labelledby="flow-bytes-title"
      onClose={onClose}
      onKeyDown={(e) => e.stopPropagation()}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      className="m-auto w-[min(1080px,calc(100vw-32px))] border border-rule-strong bg-raised p-0 text-ink shadow-pop backdrop:bg-paper/70 open:transition-[opacity,translate] open:duration-160 open:ease-out starting:open:translate-y-1 starting:open:opacity-0"
    >
      <div className="px-6 pt-5 pb-6">
        <p className="lens-eyebrow">Network · flow bytes</p>
        <h2 id="flow-bytes-title" className="mt-1 text-h3 text-ink" translate="no">
          {title}
        </h2>
        {state.kind === "ready" && (
          <p className="mt-2 text-ui text-ink-soft">
            {state.flow.packets.length.toLocaleString()} packets captured
            {state.flow.localIp && ` · local ${state.flow.localIp}${state.flow.localPort !== undefined ? `:${state.flow.localPort}` : ""}`}
          </p>
        )}

        <div className="mt-5 min-w-0 space-y-4">
          {state.kind === "loading" && <p className="text-ui text-ink-soft">Loading pcap and extracting the flow…</p>}
          {state.kind === "error" && (
            <p className="text-ui text-high-text">{state.message}</p>
          )}
          {state.kind === "ready" && (
            <>
              <HexView bytes={state.flow.client} label="Local → remote" />
              <HexView bytes={state.flow.server} label="Remote → local" />
            </>
          )}
        </div>

        <div className="mt-6 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="border border-rule-field bg-paper px-4 py-2 text-ui text-ink hover:border-ink focus:border-ink focus:outline-none"
          >
            Close
          </button>
        </div>
      </div>
    </dialog>
  );
}
